import { useState, useCallback, useRef, useEffect } from "react";
import { APIClient } from "../services/api/ApiClient";
import { CompassError } from "../types/errorTypes";
import { AxiosError, AxiosRequestConfig } from "axios";
import { useApp } from "../store/AppProvider";
import { API_ROUTES } from "../services/api/config";

interface BaseResponse<T> {
  status: string;
  message: string;
  data?: T;
}

const MAX_CONSECUTIVE_ERRORS = 3;
const BACKOFF_MULTIPLIER = 1.5;

export function usePoll<D, P>(
  condition: (data: D) => boolean,
  conditionFail?: (data: D) => boolean,
  interval = 5000,
  maxAttempts = 60,
): {
  data: D | null;
  latestResponse: D | null;
  error: CompassError | null;
  isLoading: boolean;
  startPolling: (
    request: keyof typeof API_ROUTES,
    payload: P,
    config?: AxiosRequestConfig,
  ) => void;
  stopPolling: () => void;
} {
  const { app } = useApp();
  const [data, setData] = useState<D | null>(null);
  const [latestResponse, setLatestResponse] = useState<D | null>(null);
  const [error, setError] = useState<CompassError | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const intervalId = useRef<NodeJS.Timeout | null>(null);
  const timeoutId = useRef<NodeJS.Timeout | null>(null);
  const attempts = useRef(0);
  const consecutiveErrors = useRef(0);
  const tokenRef = useRef(app.authorisation.token);

  useEffect(() => {
    tokenRef.current = app.authorisation.token;
  }, [app.authorisation.token]);

  const stopPolling = useCallback(() => {
    if (intervalId.current) {
      clearInterval(intervalId.current);
      intervalId.current = null;
    }
    if (timeoutId.current) {
      clearTimeout(timeoutId.current);
      timeoutId.current = null;
    }
    setIsLoading(false);
  }, []);

  useEffect(() => {
    return () => {
      if (intervalId.current) clearInterval(intervalId.current);
      if (timeoutId.current) clearTimeout(timeoutId.current);
    };
  }, []);

  const startPolling = useCallback(
    async (
      request: keyof typeof API_ROUTES,
      payload: P,
      config?: AxiosRequestConfig,
    ) => {
      const endpoint = API_ROUTES[request];
      const requestConfig: AxiosRequestConfig = {
        ...config,
        headers: {
          token: tokenRef.current,
          ...config?.headers,
        },
      };

      stopPolling();
      setIsLoading(true);
      setError(null);
      setData(null);
      setLatestResponse(null);
      attempts.current = 0;
      consecutiveErrors.current = 0;

      const apiClient = new APIClient();

      const poll = async (): Promise<void> => {
        if (attempts.current >= maxAttempts) {
          stopPolling();
          setError({
            step: "Polling",
            error_string: "Max polling attempts reached",
            error_user_title: "Timeout",
            error_user_message:
              "The operation is taking longer than expected. Please try again.",
          });
          return;
        }

        try {
          requestConfig.headers = {
            ...requestConfig.headers,
            token: tokenRef.current,
          };

          const response: BaseResponse<D> = await apiClient.post(
            endpoint,
            payload,
            requestConfig,
          );
          const responseData = response?.data || null;

          consecutiveErrors.current = 0;

          if (responseData) {
            setLatestResponse(responseData);
          }

          if (responseData && condition(responseData)) {
            setData(responseData);
            setError(null);
            stopPolling();
          } else if (
            conditionFail &&
            responseData &&
            conditionFail(responseData)
          ) {
            setError({
              step: "Polling",
              error_string: "Polling condition failed",
              error_user_title: "Failure Condition Met",
              error_user_message: "The polling failure condition was met",
            });
            setLatestResponse(responseData);
            stopPolling();
          } else {
            attempts.current++;
            timeoutId.current = setTimeout(poll, interval);
          }
        } catch (err) {
          consecutiveErrors.current++;

          if (consecutiveErrors.current >= MAX_CONSECUTIVE_ERRORS) {
            const compassError: CompassError = apiClient.handleError(
              err as AxiosError,
            );
            setError(compassError);
            setData(null);
            stopPolling();
          } else {
            attempts.current++;
            const backoffDelay =
              interval *
              Math.pow(BACKOFF_MULTIPLIER, consecutiveErrors.current);
            timeoutId.current = setTimeout(poll, backoffDelay);
          }
        }
      };

      poll();
    },
    [condition, conditionFail, interval, maxAttempts, stopPolling],
  );

  return { data, latestResponse, error, isLoading, startPolling, stopPolling };
}
